import { useAccountState } from '../../../../redux/reducers/accountReducer';
import { useState } from 'react';
import { FormikProps } from 'formik';
import { Button, Checkbox, Divider, makeStyles, Spinner, Text, tokens, Tooltip, Textarea } from '@fluentui/react-components';
import { Column, Row } from '../../../common';
import { Case, CaseEngagementDoc, Contact, FieldEntry, UserRef } from '../../../../data/types';
import { SelectUsersDialog } from '../../../dialogs/SelectUsersDialog';
import { LinkButton } from '../../../controls/LinkButton';
import {
    AddCircle12Regular,
    ChevronDoubleDown16Regular,
    ContactCardGroupRegular,
    ContactCardRegular,
    Dismiss12Regular,
    Sparkle16Regular,
} from '@fluentui/react-icons/fonts';

import { CopilotService } from '../../../../data/services/CopilotService';
import { useCopilotContext } from '../../../copilot/useCopilotContext';
import { PersonControl } from '../../../controls/PersonControl';
import { SelectContactsDialog } from '../../../dialogs/SelectContactsDialog';
import { FormSection } from '../../../common/FormSection';
import { EngagementDocDefaultFields } from './AddEngagementFieldDropdown';
import { useCommonStyles } from '../../../common/Styles/commonStyles';
import { ChevronDoubleUp16Regular, SparkleFilled } from '@fluentui/react-icons';
import { useUtilities } from '../../../../utilities/useUtilities';
import { PayTerms } from './EngagementEnums';

const useStyles = makeStyles({
    errorText: {
        color: tokens.colorStatusDangerForeground1,
    },
});

type EngagementFieldsProps = {
    engForm: FormikProps<CaseEngagementDoc>;
    canEdit: () => boolean;
    caseObj: Case;
    moveFieldUp: (fieldName: string) => void;
    moveFieldDown: (fieldName: string) => void;
    removeField: (e: React.MouseEvent, fieldName: string) => void;
    setSelectedTab: (fieldName: string) => void;
};
export const EngagementFields = (props: EngagementFieldsProps) => {
    const { engForm, canEdit, caseObj, moveFieldUp, moveFieldDown, removeField, setSelectedTab } = props;

    //Hook
    const { primaryAccount } = useAccountState();
    const utils = useUtilities();
    const copilotContext = useCopilotContext();

    //State
    const [generatingField, setGeneratingField] = useState<string>();
    const [generateError, setGenerateError] = useState<{ fieldName: string; message: string }>();

    //Styles
    const commonStyles = useCommonStyles();
    const styles = useStyles();

    //Access
    const canGenerate = () => {
        return !!primaryAccount?.account.features?.copilot && canEdit();
    };

    //Logic
    const addAccountSignatories = (userRefs: UserRef[]) => {
        engForm.setFieldValue('accountSignatures', [...(engForm.values.accountSignatures || []), ...userRefs]);
    };
    const removeAccountSignatory = (userRef: UserRef) => {
        engForm.setFieldValue(
            'accountSignatures',
            engForm.values.accountSignatures?.filter((x) => x.userId != userRef.userId) || [],
        );
    };
    const addClientSignatories = (contacts: Contact[]) => {
        engForm.setFieldValue('clientSignatures', [...(engForm.values.clientSignatures || []), ...contacts]);
    };
    const removeClientSignatories = (contact: Contact) => {
        engForm.setFieldValue(
            'clientSignatures',
            engForm.values.clientSignatures?.filter((x) => x.contactId != contact.contactId) || [],
        );
    };

    //Setting Billing Terms
    const getSuggestedFixedFeeText = (amount?: string) => {
        return `This engagement covers the scope of work described herein, billed at a fixed fee of [${utils.formatCurrency(amount || '0')}]. This fee reflects a bundled rate for the defined deliverables and agreed-upon milestones, regardless of actual hours worked, and assumes timely client feedback and access to necessary resources. Any work beyond the outlined scope may be subject to a separate agreement or change order.`;
    };
    const getSuggestedHourlyRateText = () => {
        return 'Services will be billed on a monthly basis, with invoices issued at the beginning of each month for work completed in the prior billing cycle. Billable time is tracked and charged in increments of [one-tenth of an hour (6-minute intervals)] to ensure accurate and fair accounting.';
    };
    const setBillingTermsSuggestedText = (fieldIndex: number) => {
        if (caseObj?.isFixedFee) {
            engForm.setFieldValue(`fields.[${fieldIndex}].fieldValue`, getSuggestedFixedFeeText(caseObj?.fixedAmount));
        } else {
            engForm.setFieldValue(`fields.[${fieldIndex}].fieldValue`, getSuggestedHourlyRateText());
        }
    };

    //Setting Payment Terms
    const getPayTerms = (payTerms?: string) => {
        if (payTerms == PayTerms.net30 || payTerms == PayTerms.net60 || payTerms == PayTerms.net90) {
            const days =
                payTerms == PayTerms.net30
                    ? '30'
                    : payTerms == PayTerms.net60
                      ? '60'
                      : payTerms == PayTerms.net90
                        ? '90'
                        : '0';
            return `All payments are due within [${days}] calendar days of the invoice date [(${payTerms})].`;
        } else if (payTerms == PayTerms.uponReceipt) {
            return `All payments are due upon receipt of the invoice.`;
        } else return '';
    };
    const getPaymentTermsText = (paymentBy?: string) => {
        return !!paymentBy
            ? `${paymentBy ? getPayTerms(paymentBy) : ''} Late payments may be subject to a finance charge of [1.5%] per month or the maximum rate permitted by law. If payment is not received within the specified timeframe, the provider reserves the right to pause services until the account is brought current. Accepted payment methods include ACH transfer, check, or credit card (subject to applicable processing fees).`
            : '';
    };
    const setPaymentTermsLanguage = (fieldIndex: number) => {
        engForm.setFieldValue(`fields.[${fieldIndex}].fieldValue`, getPaymentTermsText(PayTerms.net60));
    };

    const getFieldAction = (fieldName: string, index: number) => {
        if (fieldName == EngagementDocDefaultFields.billingTerms) return () => setBillingTermsSuggestedText(index);
        else if (fieldName == EngagementDocDefaultFields.paymentTerms) return () => setPaymentTermsLanguage(index);
        else return undefined;
    };

    //Casey field generation
    const buildRegeneratePrompt = (fieldName: string, fieldValue?: string) => {
        const caseDetails = [
            `Case name: ${caseObj?.caseName || 'not specified'}`,
            `Case type: ${caseObj?.caseType || 'not specified'}`,
            caseObj?.overview ? `Case overview: ${caseObj.overview}` : undefined,
            caseObj?.isFixedFee
                ? `Billing: fixed fee of ${utils.formatCurrency(caseObj?.fixedAmount || '0')}`
                : 'Billing: hourly',
        ]
            .filter(Boolean)
            .join('\n');

        const instruction = fieldValue?.trim()
            ? `Rewrite and improve the existing text below. Keep its intent, any stated facts, and any [bracketed placeholders] intact.\n\n--- Existing "${fieldName}" text ---\n${fieldValue.trim()}\n--- End existing text ---`
            : 'No text exists yet for this section. Draft appropriate language based on the case details above.';

        return (
            `You are drafting the "${fieldName}" section of a client engagement letter.\n\n` +
            `${caseDetails}\n\n${instruction}\n\n` +
            'This is an automated request, so reply with ONLY the replacement section text. ' +
            'Do not include an opening restatement, the section title, headings, bullet points, markdown, preamble, or closing remarks. ' +
            'Do not call any tools.'
        );
    };
    const regenerateFieldAsync = async (fieldName: string, fieldValue: string, index: number) => {
        setGenerateError(undefined);
        setGeneratingField(fieldName);

        try {
            const context = await copilotContext.getContext();
            const text = (
                await new CopilotService(primaryAccount!).sendSilentMessage(
                    buildRegeneratePrompt(fieldName, fieldValue),
                    undefined,
                    context,
                )
            ).trim();

            if (!text) {
                setGenerateError({ fieldName, message: 'Casey did not return any text. Please try again.' });
                return;
            }

            engForm.setFieldValue(`fields.[${index}].fieldValue`, text);
        } catch (error) {
            setGenerateError({
                fieldName,
                message: error instanceof Error ? error.message : 'Unable to generate text for this field.',
            });
        } finally {
            setGeneratingField(undefined);
        }
    };
    const getEngagementField = (fieldName: string, fieldValue: string, index: number, length?: number) => {
        const action = getFieldAction(fieldName, index);
        const normalizedFieldName = (fieldName || '').toLowerCase().replace(/[^a-z0-9]+/g, '-');

        const handleSuggestText = action
            ? (ev: React.MouseEvent<HTMLButtonElement>) => {
                  ev.stopPropagation();
                  action();
              }
            : undefined;

        const handleMoveUp = (ev: React.MouseEvent<HTMLButtonElement>) => {
            ev.stopPropagation();
            moveFieldUp(fieldName || '');
        };
        const handleMoveDown = (ev: React.MouseEvent<HTMLButtonElement>) => {
            ev.stopPropagation();
            moveFieldDown(fieldName || '');
        };
        const handleRemoveField = (ev: React.MouseEvent<HTMLButtonElement>) => {
            ev.stopPropagation();
            removeField(ev as React.MouseEvent, fieldName);
        };
        const handleRegenerateField = (ev: React.MouseEvent<HTMLButtonElement>) => {
            ev.stopPropagation();
            regenerateFieldAsync(fieldName, fieldValue, index);
        };

        const isMilestonesField = fieldName == EngagementDocDefaultFields.milestones;
        const isGenerating = generatingField == fieldName;
        const fieldError = generateError?.fieldName == fieldName ? generateError.message : undefined;

        return (
            <FormSection
                key={fieldName}
                title={fieldName}
                padding='pr-1'
                backgroundColor={tokens.colorNeutralCardBackground}
                action={
                    <Row gap='medium' noWrap>
                        {canGenerate() && !isMilestonesField && (
                            <Column vAlign='middle' width='auto'>
                                <Tooltip
                                    content={
                                        fieldValue?.trim()
                                            ? `Ask Casey to rewrite the ${fieldName} text`
                                            : `Ask Casey to draft the ${fieldName} text`
                                    }
                                    relationship='label'
                                >
                                    <Button
                                        appearance='secondary'
                                        size='small'
                                        icon={isGenerating ? <Spinner size='extra-tiny' /> : <SparkleFilled color='gold' />}
                                        disabled={!!generatingField}
                                        onClick={handleRegenerateField}
                                        data-testid={`engagement-page-casey-generate-btn-${normalizedFieldName}`}
                                    />
                                </Tooltip>
                            </Column>
                        )}
                        <Divider vertical />
                        <Row gap='small'>
                            <Button
                                className={commonStyles.button.root}
                                appearance='secondary'
                                size='small'
                                icon={<ChevronDoubleUp16Regular />}
                                onClick={handleMoveUp}
                                disabled={index == 0}
                                data-testid={`engagement-page-move-up-btn-${normalizedFieldName}`}
                            />
                            <Button
                                className={commonStyles.button.root}
                                appearance='secondary'
                                size='small'
                                icon={<ChevronDoubleDown16Regular />}
                                onClick={handleMoveDown}
                                disabled={index == length! - 1}
                                data-testid={`engagement-page-move-down-btn-${normalizedFieldName}`}
                            />
                            <Button
                                className={commonStyles.button.root}
                                appearance='secondary'
                                size='small'
                                icon={<Dismiss12Regular color='red' />}
                                onClick={handleRemoveField}
                                data-testid={`engagement-page-remove-field-btn-${normalizedFieldName}`}
                            />
                        </Row>
                    </Row>
                }
            >
                {fieldName == EngagementDocDefaultFields.milestones ? (
                    <Column subtleBorder borderRadius='5px' padding='px-2 py-2'>
                        <Text weight='semibold'>This will show the Milestones as defined in the Case.</Text>
                    </Column>
                ) : (
                    <Column gap='small'>
                        <Textarea
                            name={`fields.[${index}].fieldValue`}
                            data-name={fieldName}
                            tabIndex={index}
                            rows={6}
                            readOnly={!canEdit()}
                            value={fieldValue}
                            onChange={engForm?.handleChange}
                            onFocus={() => setSelectedTab(fieldName as string)}
                            placeholder={`Enter ${fieldName}`}
                            data-testid={`engagement-page-field-textarea-${normalizedFieldName}`}
                        />
                        {!!fieldError && (
                            <Text className={styles.errorText} size={200}>
                                {fieldError}
                            </Text>
                        )}
                    </Column>
                )}
            </FormSection>
        );
    };

    return (
        <>
            {/* All the Engagement fields */}
            {engForm?.values?.fields?.map((field: FieldEntry, index, arr) => (
                <Column padding='px-2' key={field.fieldName}>
                    {<>{getEngagementField(field.fieldName!, field.fieldValue!, index, arr.length)}</>}
                </Column>
            ))}

            {/* Signatures Section */}
            <Column padding='py-3 px-3' height='auto'>
                <Text size={400} italic>
                    Signatures
                </Text>
                <Divider style={{ marginBlockStart: '-.5rem' }} />
                <Checkbox
                    label='Add Signatures'
                    name='addSignatures'
                    checked={engForm.values.addSignatures}
                    onChange={engForm.handleChange}
                    data-name='signatures'
                    disabled={!canEdit()}
                    data-testid='engagement-page-add-signatures-checkbox'
                />
                {engForm.values.addSignatures && (
                    <Row gap='large'>
                        <Column flex='1' minWidth='300px' gap='small'>
                            <Row>
                                <Column vAlign='middle'>
                                    <Text weight='semibold'>Signatures from {primaryAccount?.account.accountName}</Text>
                                </Column>
                                <Column width='auto' vAlign='middle'>
                                    {canEdit() && (
                                        <SelectUsersDialog
                                            title='Add Signatories'
                                            icon={<ContactCardGroupRegular />}
                                            assignedUsers={engForm.values.accountSignatures}
                                            onSubmit={addAccountSignatories}
                                        >
                                            <LinkButton icon={<AddCircle12Regular />} text='Add Signatory' />
                                        </SelectUsersDialog>
                                    )}
                                </Column>
                            </Row>
                            <Divider style={{ maxHeight: '.25rem' }} />
                            <Column>
                                {engForm.values.accountSignatures?.map((userRef: UserRef, index: number) => (
                                    <PersonControl
                                        key={userRef?.userId || index}
                                        size='medium'
                                        person={userRef}
                                        onRemove={!!canEdit ? () => removeAccountSignatory(userRef) : undefined}
                                    />
                                ))}
                            </Column>
                        </Column>
                        <Column flex='1' minWidth='300px' gap='small'>
                            <Row>
                                <Column vAlign='middle'>
                                    <Text weight='semibold'>Signatures from {caseObj.retainedBy?.party?.name}</Text>
                                </Column>
                                <Column width='auto' vAlign='middle'>
                                    {!!canEdit() && (
                                        <SelectContactsDialog
                                            title='Add Signatories'
                                            icon={<ContactCardRegular />}
                                            party={caseObj.retainedBy?.party}
                                            contacts={caseObj.retainedBy?.contacts}
                                            onSubmit={addClientSignatories}
                                            noCreate
                                            filter={(contact) => {
                                                const isAlreadyAdded = engForm.values.clientSignatures?.some(
                                                    (c) => c.contactId === contact.contactId,
                                                );
                                                return !isAlreadyAdded;
                                            }}
                                        >
                                            <LinkButton icon={<AddCircle12Regular />} text='Add Signatory' />
                                        </SelectContactsDialog>
                                    )}
                                </Column>
                            </Row>
                            <Divider style={{ maxHeight: '.25rem' }} />
                            <Column>
                                {engForm.values.clientSignatures?.map((contact: Contact) => (
                                    <PersonControl
                                        key={contact?.contactId}
                                        size='medium'
                                        person={contact}
                                        onRemove={!!canEdit() ? () => removeClientSignatories(contact) : undefined}
                                    />
                                ))}
                            </Column>
                        </Column>
                    </Row>
                )}
            </Column>
        </>
    );
};
